import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { useEffect, useState, lazy, Suspense } from "react";
import { Toaster } from "sonner";
import { trackEvent } from "@/lib/analytics";
import { loadContentImages } from "@/lib/cloudinary";
import Layout from "@/components/Layout";
import "@/App.css";

// Route-based code splitting: every page ships as its own async chunk so the
// initial (shared) bundle stays tiny. Each page's page-specific content data
// (problem clusters, comparisons, local areas, etc.) is pulled into that page's
// own chunk instead of the main bundle. react-snap prerenders each route to
// static HTML, so first paint / SEO are unaffected; only hydration JS is split.
const Home = lazy(() => import("@/pages/Home"));
const About = lazy(() => import("@/pages/About"));
const CaseStudies = lazy(() => import("@/pages/CaseStudies"));
const Investigations = lazy(() => import("@/pages/Investigations"));const CaseStudyCategory = lazy(() => import("@/pages/CaseStudyCategory"));
const CaseStudyDetail = lazy(() => import("@/pages/CaseStudyDetail"));
const AllInvestigations = lazy(() => import("@/pages/AllInvestigations"));
const BuildingPhysicsAssessment = lazy(() => import("@/pages/BuildingPhysicsAssessment"));
const BuildingPerformanceInvestigation = lazy(() => import("@/pages/BuildingPerformanceInvestigation"));
const SampleReport = lazy(() => import("@/pages/SampleReport"));
const ProblemInvestigationPage = lazy(() => import("@/pages/ProblemInvestigationPage"));
const CommonHouseProblems = lazy(() => import("@/pages/CommonHouseProblems"));
const CaseStudyLoftThermal = lazy(() => import("@/pages/CaseStudyLoftThermal"));
const BuildingPhysicsEnquiry = lazy(() => import("@/pages/BuildingPhysicsEnquiry"));
const PassiveHouseLanding = lazy(() => import("@/pages/PassiveHouseLanding"));
const PassiveHouseEnquiry = lazy(() => import("@/pages/PassiveHouseEnquiry"));
const EngineeringDesignServices = lazy(() => import("@/pages/EngineeringDesignServices"));
const CategoryPillar = lazy(() => import("@/pages/CategoryPillar"));
const BuildingPerformanceQA = lazy(() => import("@/pages/BuildingPerformanceQA"));
const QAEnquiry = lazy(() => import("@/pages/QAEnquiry"));
const Gallery = lazy(() => import("@/pages/Gallery"));
const GalleryImagePage = lazy(() => import("@/pages/GalleryImagePage"));
const InvestigationPage = lazy(() => import("@/pages/InvestigationPage"));
const Reviews = lazy(() => import("@/pages/Reviews"));
const Contact = lazy(() => import("@/pages/Contact"));
const ServicePage = lazy(() => import("@/pages/ServicePage"));
const AreaRouter = lazy(() => import("@/pages/AreaRouter"));
const AreasIndex = lazy(() => import("@/pages/AreasIndex"));
const Professionals = lazy(() => import("@/pages/Professionals"));
const ProfessionalPage = lazy(() => import("@/pages/ProfessionalPage"));
const SelfAssessment = lazy(() => import("@/pages/SelfAssessment"));
const WhyDampFails = lazy(() => import("@/pages/WhyDampFails"));
const Learn = lazy(() => import("@/pages/Learn"));
const Pillar = lazy(() => import("@/pages/Pillar"));
const Article = lazy(() => import("@/pages/Article"));
const ProjectMap = lazy(() => import("@/pages/ProjectMap"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("@/pages/CookiePolicy"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const ExportVertical = lazy(() => import("@/pages/ExportVertical"));
// Admin dashboard (media library, uploads, SEO/performance tools) is noindex and
// admin-only — lazy-loaded so its heavy code is NOT shipped to public visitors.
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const Comparisons = lazy(() => import("@/pages/Comparisons"));
const ComparisonCategory = lazy(() => import("@/pages/ComparisonCategory"));
const ComparisonPage = lazy(() => import("@/pages/ComparisonPage"));
const Problems = lazy(() => import("@/pages/Problems"));
const ProblemCategory = lazy(() => import("@/pages/ProblemCategory"));
const ProblemHub = lazy(() => import("@/pages/ProblemHub"));
const ProblemPage = lazy(() => import("@/pages/ProblemPage"));

// Analytics enrichment for click tracking on /problems/* pages. The heavy
// problems dataset is NOT statically imported (that would bloat the main
// bundle); it is dynamically loaded and cached the moment a visitor lands on a
// /problems route, so it is ready by the time they click a phone/email link.
let _problemsMod = null;
const preloadProblems = () => {
  if (!_problemsMod) {
    import("@/data/problems").then((m) => { _problemsMod = m; }).catch(() => {});
  }
};

// Legacy authored case-study URLs moved from /investigations/* to /case-studies/*
// (Batch 6). Single-hop 301s; the destination pages never redirect further.
function InvestigationsToCaseStudies() {
  const { category, slug } = useParams();
  const { hash } = useLocation();
  const target = slug ? `/case-studies/${category}/${slug}` : `/case-studies/${category}`;
  return <Navigate to={`${target}${hash || ""}`} replace />;
}

// Watches route changes to preload the problems dataset (used by analytics) and
// keep behaviour identical to the previous static-import approach.
function RoutePreloader() {
  const loc = useLocation();
  useEffect(() => {
    if (loc.pathname.startsWith("/problems")) preloadProblems();
  }, [loc.pathname]);
  return null;
}

const PageFallback = <div style={{ minHeight: "60vh" }} aria-hidden="true" />;

function App() {
  // Load the Media Library (DAM) content-image map once, then force a single
  // re-render so migrated marketing/report-preview images are served from
  // Cloudinary (single source of truth) instead of the local physical files.
  const [, setImagesReady] = useState(false);
  useEffect(() => {
    loadContentImages().then(() => setImagesReady(true));
  }, []);

  // Resolve the current route into an analytics context so phone/email/WhatsApp
  // clicks carry the article/hub/category the visitor was on when they converted.
  const getRouteContext = () => {
    try {
      const parts = window.location.pathname.split("/").filter(Boolean);
      if (parts[0] === "problems" && _problemsMod) {
        if (parts[1] === "hubs" && parts[2]) {
          const hub = _problemsMod.getProblemHub(parts[2]);
          if (hub) return { page_type: "problem_hub", hub: hub.slug, hub_name: hub.name };
        } else if (parts[1] && parts[2]) {
          const p = _problemsMod.getProblem(parts[1], parts[2]);
          if (p) return _problemsMod.getProblemAnalyticsContext(p);
        }
      }
      return { page_type: "page", page_path: window.location.pathname };
    } catch {
      return {};
    }
  };

  // Site-wide delegated listener: fire GA4 phone_click / email_click for every
  // tel: / mailto: link, wherever it appears (header, footer, FABs, pages).
  useEffect(() => {
    const onClick = (e) => {
      const a = e.target?.closest?.("a[href]");
      if (!a) return;
      const href = a.getAttribute("href") || "";
      const link_text = (a.textContent || "").trim().slice(0, 100);
      const tid = a.getAttribute("data-testid") || "";
      const ctx = getRouteContext();
      if (href.startsWith("tel:")) {
        trackEvent("phone_click", { link_url: href, link_text, phone_number: href.replace(/^tel:/, ""), ...ctx });
      } else if (href.startsWith("mailto:")) {
        trackEvent("email_click", { link_url: href, link_text, email_address: href.replace(/^mailto:/, "").split("?")[0], ...ctx });
      } else if (href.startsWith("whatsapp:") || /(?:^|\/\/)(?:[a-z.]*\.)?(?:wa\.me|whatsapp\.com)/i.test(href)) {
        const location = tid === "fab-whatsapp" ? "floating_action" : (tid || "inline_link");
        trackEvent("whatsapp_click", { link_url: href, link_text, location, ...ctx });
      }
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  return (
    <BrowserRouter>
      <RoutePreloader />
      <Toaster
        position="top-right"
        richColors
        toastOptions={{ style: { fontFamily: "IBM Plex Sans, sans-serif" } }}
      />
      <Suspense fallback={PageFallback}>
      <Routes>
        <Route path="/export/vertical" element={<ExportVertical />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route
          path="/*"
          element={
            <Layout>
              <Routes>
                <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/building-physics-assessment" element={<BuildingPhysicsAssessment />} />
          <Route path="/building-performance-investigation" element={<BuildingPerformanceInvestigation />} />
          {/* Sample reports — add a new <Route> + registry entry to publish another. */}
          <Route path="/sample-building-performance-investigation-report" element={<SampleReport slug="building-performance-investigation" />} />
          <Route path="/case-studies/loft-thermal-investigation" element={<CaseStudyLoftThermal />} />
          <Route path="/building-physics-assessment/enquiry" element={<BuildingPhysicsEnquiry />} />
          <Route path="/engineering-design-services" element={<EngineeringDesignServices />} />
          <Route path="/building-performance-diagnostics" element={<CategoryPillar slug="building-performance-diagnostics" />} />
          <Route path="/building-performance-engineering" element={<CategoryPillar slug="building-performance-engineering" />} />
          <Route path="/building-performance-quality-assurance" element={<BuildingPerformanceQA />} />
          <Route path="/building-performance-quality-assurance/enquiry" element={<QAEnquiry />} />
          <Route path="/services/passive-house-design" element={<PassiveHouseLanding slug="passive-house-design" />} />
          <Route path="/services/enerphit-retrofit-design" element={<PassiveHouseLanding slug="enerphit-retrofit-design" />} />
          <Route path="/services/phpp-energy-modelling" element={<PassiveHouseLanding slug="phpp-energy-modelling" />} />
          <Route path="/services/passive-house-certification-support" element={<PassiveHouseLanding slug="passive-house-certification-support" />} />
          <Route path="/passive-house-design/enquiry" element={<PassiveHouseEnquiry />} />
          {/* Problem-investigation SEO landing pages — static paths win over /services/:slug. */}
          <Route path="/common-house-problems" element={<CommonHouseProblems />} />
          <Route path="/services/draughts-air-leakage-investigation-london" element={<ProblemInvestigationPage slug="draughts-air-leakage-investigation-london" />} />
          <Route path="/services/cold-home-heat-loss-investigation-london" element={<ProblemInvestigationPage slug="cold-home-heat-loss-investigation-london" />} />
          <Route path="/services/condensation-mould-moisture-investigation-london" element={<ProblemInvestigationPage slug="condensation-mould-moisture-investigation-london" />} />
          <Route path="/services/cold-walls-investigation-london" element={<ProblemInvestigationPage slug="cold-walls-investigation-london" />} />
          <Route path="/services/cold-floors-investigation-london" element={<ProblemInvestigationPage slug="cold-floors-investigation-london" />} />
          <Route path="/services/cold-rooms-investigation-london" element={<ProblemInvestigationPage slug="cold-rooms-investigation-london" />} />
          <Route path="/services/wall-heat-loss-investigation-london" element={<ProblemInvestigationPage slug="wall-heat-loss-investigation-london" />} />
          <Route path="/services/loft-heat-loss-insulation-investigation-london" element={<ProblemInvestigationPage slug="loft-heat-loss-insulation-investigation-london" />} />
          <Route path="/services/floor-heat-loss-insulation-investigation-london" element={<ProblemInvestigationPage slug="floor-heat-loss-insulation-investigation-london" />} />
          <Route path="/services/ceiling-heat-loss-investigation-london" element={<ProblemInvestigationPage slug="ceiling-heat-loss-investigation-london" />} />
          <Route path="/services/thermal-bridge-investigation-london" element={<ProblemInvestigationPage slug="thermal-bridge-investigation-london" />} />
          <Route path="/services/high-heating-bills-energy-investigation-london" element={<ProblemInvestigationPage slug="high-heating-bills-energy-investigation-london" />} />
          <Route path="/services/condensation-on-windows-investigation-london" element={<ProblemInvestigationPage slug="condensation-on-windows-investigation-london" />} />
          <Route path="/services/black-mould-investigation-london" element={<ProblemInvestigationPage slug="black-mould-investigation-london" />} />
          <Route path="/services/damp-walls-investigation-london" element={<ProblemInvestigationPage slug="damp-walls-investigation-london" />} />
          <Route path="/services/high-indoor-humidity-investigation-london" element={<ProblemInvestigationPage slug="high-indoor-humidity-investigation-london" />} />
          <Route path="/services/poor-indoor-air-quality-investigation-london" element={<ProblemInvestigationPage slug="poor-indoor-air-quality-investigation-london" />} />
          <Route path="/services/home-ventilation-assessment-london" element={<ProblemInvestigationPage slug="home-ventilation-assessment-london" />} />
          <Route path="/services/overheating-investigation-london" element={<ProblemInvestigationPage slug="overheating-investigation-london" />} />
          <Route path="/services/loft-condensation-investigation-london" element={<ProblemInvestigationPage slug="loft-condensation-investigation-london" />} />
          <Route path="/services/uneven-room-temperatures-investigation-london" element={<ProblemInvestigationPage slug="uneven-room-temperatures-investigation-london" />} />
          <Route path="/services/cold-bathroom-investigation-london" element={<ProblemInvestigationPage slug="cold-bathroom-investigation-london" />} />
          <Route path="/services/:slug" element={<ServicePage />} />
          <Route path="/services/:slug/:area" element={<ServicePage />} />
          <Route path="/areas" element={<AreasIndex />} />
          <Route path="/areas/:area" element={<AreaRouter />} />
          <Route path="/comparisons" element={<Comparisons />} />
          <Route path="/comparisons/:category" element={<ComparisonCategory />} />
          <Route path="/comparisons/:category/:slug" element={<ComparisonPage />} />
          <Route path="/problems" element={<Problems />} />
          <Route path="/problems/hubs/:hub" element={<ProblemHub />} />
          <Route path="/problems/:category" element={<ProblemCategory />} />
          <Route path="/problems/:category/:slug" element={<ProblemPage />} />
          <Route path="/professionals" element={<Professionals />} />
          <Route path="/professionals/:slug" element={<ProfessionalPage />} />
          <Route path="/who-we-help" element={<Navigate to="/professionals" replace />} />
          <Route path="/self-assessment" element={<SelfAssessment />} />
          <Route path="/learn" element={<Learn />} />
          <Route path="/learn/guides/:cluster" element={<Pillar />} />
          <Route path="/learn/why-damp-treatments-fail" element={<WhyDampFails />} />
          <Route path="/learn/:slug" element={<Article />} />
          <Route path="/projects" element={<ProjectMap />} />
          <Route path="/projects-map" element={<ProjectMap />} />
          <Route path="/investigations" element={<Investigations />} />
          <Route path="/investigations/all" element={<Navigate to="/case-studies/all" replace />} />
          <Route path="/investigations/:category" element={<InvestigationsToCaseStudies />} />
          <Route path="/investigations/:category/:slug" element={<InvestigationsToCaseStudies />} />
          <Route path="/case-studies" element={<CaseStudies />} />
          <Route path="/case-studies/all" element={<AllInvestigations />} />
          <Route path="/case-studies/:category" element={<CaseStudyCategory />} />
          <Route path="/case-studies/:category/:slug" element={<CaseStudyDetail />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/gallery/:slug" element={<GalleryImagePage />} />
          <Route path="/investigation/:slug" element={<InvestigationPage />} />
          <Route path="/reviews" element={<Reviews />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="*" element={<NotFound />} />
              </Routes>
            </Layout>
          }
        />
      </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
